import { copy } from 'copy-anything';
import { pick } from 'filter-anything';
import { isFullString, isNumber, isArray, isString, isPlainObject } from 'is-what';
import { merge } from 'merge-anything';
import { getProp } from 'path-to-prop';
import { sort } from 'fast-sort';

function writeActionFactory(data, vue2StoreOptions, actionName, makeBackup) {
    const { vueInstance: vue } = vue2StoreOptions;
    return function ({ payload, collectionPath, docId, pluginModuleConfig, }) {
        // write actions cannot be executed on collections
        if (!docId)
            throw new Error('An non-existent action was triggered on a collection');
        const collectionDic = data[collectionPath];
        if (makeBackup)
            makeBackup(collectionPath, docId);
        // always start from an empty document on 'replace' or when the doc is non existent
        if (actionName === 'replace' || !collectionDic[docId]) {
            vue.set(collectionDic, docId, {});
        }
        const docDataToMutate = collectionDic[docId];
        if (!docDataToMutate)
            throw new Error(`Document data not found for id: ${collectionPath} ${docId}`);
        if (actionName === 'merge') {
            Object.entries(payload).forEach(([key, value]) => {
                vue.set(docDataToMutate, key, merge(docDataToMutate[key], value));
            });
        }
        if (actionName === 'assign' || actionName === 'replace') {
            Object.entries(payload).forEach(([key, value]) => {
                vue.set(docDataToMutate, key, value);
            });
        }
    };
}

function insertActionFactory(data, vue2StoreOptions, makeBackup) {
    const { generateRandomId, vueInstance: vue } = vue2StoreOptions;
    return function ({ payload, collectionPath, docId, pluginModuleConfig, }) {
        const collectionDic = data[collectionPath];
        const _docId = docId ||
            (isFullString(payload.id) || isNumber(payload.id) ? String(payload.id) : generateRandomId());
        if (makeBackup)
            makeBackup(collectionPath, _docId);
        const docDataToMutate = collectionDic[_docId];
        // we're setting a new doc
        if (!docDataToMutate) {
            vue.set(collectionDic, _docId, payload);
            return _docId;
        }
        // we're overwriting a doc with a new doc
        Object.entries(payload).forEach(([key, value]) => {
            vue.set(docDataToMutate, key, value);
        });
        return _docId;
    };
}

function deletePropActionFactory(data, vue2StoreOptions, makeBackup) {
    return function ({ payload, collectionPath, docId, pluginModuleConfig, }) {
        const { vueInstance: vue } = vue2StoreOptions;
        // `deleteProp` action cannot be executed on collections
        if (!docId)
            throw new Error('An non-existent action was triggered on a collection');
        const collectionDic = data[collectionPath];
        const docData = collectionDic[docId];
        if (!docData)
            throw new Error(`Document data not found for id: ${collectionPath} ${docId}`);
        if (makeBackup)
            makeBackup(collectionPath, docId);
        const payloadArray = isArray(payload) ? payload : [payload];
        for (const propToDelete of payloadArray) {
            const isNestedPropPath = /[./]/.test(propToDelete);
            if (isNestedPropPath) {
                const parts = propToDelete.split(/[./]/);
                const lastPart = parts.pop();
                const parentRef = getProp(docData, parts.join('.'));
                vue.delete(parentRef, lastPart || '');
            }
            else {
                vue.delete(docData, propToDelete);
            }
        }
    };
}

function deleteActionFactory(data, vue2StoreOptions, makeBackup) {
    return function ({ payload, collectionPath, docId, pluginModuleConfig, }) {
        const { vueInstance: vue } = vue2StoreOptions;
        const _docId = docId || payload;
        if (!isFullString(_docId))
            throw new Error('No ID passed to delete action.');
        if (makeBackup)
            makeBackup(collectionPath, _docId);
        vue.delete(data[collectionPath], _docId);
    };
}

function fetchActionFactory(data, vue2StoreOptions) {
    return function ({ payload, collectionPath, docId, actionConfig, pluginModuleConfig, }) {
        const optimisticFetch = !payload || !Object.hasOwnProperty.call(payload || {}, 'force') || (payload === null || payload === void 0 ? void 0 : payload.force) === false;
        if (optimisticFetch) {
            const collectionData = data[collectionPath];
            if (!docId && Object.keys(collectionData).length > 0) {
                const localDocs = Object.entries(collectionData).map(([_docId, data]) => ({
                    data,
                    exists: 'unknown',
                    id: _docId,
                }));
                const fetchResponse = { docs: localDocs };
                return fetchResponse;
            }
            if (docId) {
                const localDoc = collectionData[docId];
                // if already fetched
                if (localDoc) {
                    const fetchResponse = {
                        docs: [
                            {
                                data: localDoc,
                                exists: 'unknown',
                                id: docId,
                            },
                        ],
                    };
                    return fetchResponse;
                }
            }
        }
        const doOnFetchAction = (_payload, meta) => {
            // abort updating local state if the payload is undefined
            if (_payload === undefined)
                return;
            insertActionFactory(data, vue2StoreOptions)({
                payload: _payload,
                collectionPath,
                docId,
                actionConfig,
                pluginModuleConfig,
            });
        };
        return doOnFetchAction;
    };
}

function streamActionFactory(data, vue2StoreOptions) {
    return function ({ payload, collectionPath, docId, actionConfig, pluginModuleConfig, mustExecuteOnRead, }) {
        // hover over the prop names below to see more info on when they are triggered:
        const doOnStream = {
            added: (payload, meta) => {
                // abort updating local state if the payload was set to undefined
                if (payload === undefined)
                    return;
                const _docId = docId || `${meta.id}`;
                insertActionFactory(data, vue2StoreOptions)({
                    payload,
                    collectionPath,
                    docId: _docId,
                    actionConfig,
                    pluginModuleConfig,
                });
            },
            modified: (payload, meta) => {
                // abort updating local state if the payload was set to undefined
                if (payload === undefined)
                    return;
                const _docId = docId || `${meta.id}`;
                insertActionFactory(data, vue2StoreOptions)({
                    payload,
                    collectionPath,
                    docId: _docId,
                    actionConfig,
                    pluginModuleConfig,
                });
            },
            removed: (payload, meta) => {
                // abort updating local state if the payload was set to undefined
                if (payload === undefined)
                    return;
                const collectionPathDocIdToDelete = isFullString(docId)
                    ? [collectionPath, docId]
                    : isString(payload)
                        ? [collectionPath, payload]
                        : [collectionPath, meta.id];
                const [_cPath, _dId] = collectionPathDocIdToDelete;
                deleteActionFactory(data, vue2StoreOptions)({
                    payload: undefined,
                    collectionPath: _cPath,
                    docId: _dId,
                    actionConfig,
                    pluginModuleConfig,
                });
            },
        };
        return doOnStream;
    };
}

function revertActionFactory(data, vue2StoreOptions, restoreBackup) {
    return function ({ payload, collectionPath, docId, pluginModuleConfig, actionName, error, }) {
        // revert all write actions when called on a doc
        if (docId &&
            ['insert', 'merge', 'assign', 'replace', 'delete', 'deleteProp'].includes(actionName)) {
            restoreBackup(collectionPath, docId);
            return;
        }
        // insert on collection (no id)
        if (!docId && actionName === 'insert')
            actionName = 'insert on collections';
        // haven't implemented reverting 'fetch', 'stream' actions yet
        console.error(`[@magnetarjs/plugin-vue2] revert not yet implemented for ${actionName}`);
    };
}

/**
 * Filters a Collection module's data based on provided clauses.
 *
 * @param {Record<string, Record<string, any>>} collectionDB
 * @param {Clauses} clauses
 * @returns {'no-filter' | [string, Record<string, any>][]} filtered and sorted entries
 */
function filterDataPerClauses(collectionDB, clauses) {
    const { where = [], orderBy = [], limit } = clauses;
    // return 'no-filter' if no filtering is needed
    if (!where.length && !orderBy.length && !isNumber(limit))
        return 'no-filter';
    // all other cases we need to create a new Map() with the results
    const entries = [];
    Object.entries(collectionDB).forEach(([docId, docData]) => {
        const passesWhereFilters = where.every((whereQuery) => {
            const [fieldPath, operator, expectedValue] = whereQuery;
            const valueAtFieldPath = getProp(docData, fieldPath);
            let passes = false;
            switch (operator) {
                case '==':
                    passes = valueAtFieldPath == expectedValue;
                    break;
                case '!=':
                    passes = valueAtFieldPath != expectedValue;
                    break;
                case '<':
                    passes = valueAtFieldPath < expectedValue;
                    break;
                case '<=':
                    passes = valueAtFieldPath <= expectedValue;
                    break;
                case '>':
                    passes = valueAtFieldPath > expectedValue;
                    break;
                case '>=':
                    passes = valueAtFieldPath >= expectedValue;
                    break;
                case 'in':
                    passes = isArray(expectedValue) && expectedValue.includes(valueAtFieldPath);
                    break;
                case 'not-in':
                    passes = isArray(expectedValue) && !expectedValue.includes(valueAtFieldPath);
                    break;
                case 'array-contains':
                    passes = isArray(valueAtFieldPath) && valueAtFieldPath.includes(expectedValue);
                    break;
                case 'array-contains-any':
                    passes =
                        isArray(valueAtFieldPath) &&
                            valueAtFieldPath.some((v) => isArray(expectedValue) && expectedValue.includes(v));
                    break;
                default:
                    throw new Error('invalid operator');
            }
            return passes;
        });
        if (!passesWhereFilters)
            return;
        entries.push([docId, docData]);
    });
    // orderBy
    const by = orderBy.reduce((carry, [path, direction = 'asc']) => {
        const sorter = {
            [direction]: (entry) => getProp(entry[1], path),
        };
        carry.push(sorter);
        return carry;
    }, []);
    const entriesOrdered = orderBy.length ? sort(entries).by(by) : entries;
    // limit
    const entriesLimited = isNumber(limit) ? entriesOrdered.slice(0, limit) : entriesOrdered;
    return entriesLimited;
}
function objectToMap(object, originalObjectToClear, entriesInCustomOrder) {
    const dic = object || {};
    function get(id) {
        return dic[id];
    }
    function has(id) {
        return !!dic[id];
    }
    function keys() {
        if (entriesInCustomOrder)
            return entriesInCustomOrder.map((e) => e[0]);
        return Object.keys(dic);
        // return IterableIterator<string>
    }
    function values() {
        if (entriesInCustomOrder)
            return entriesInCustomOrder.map((e) => e[1]);
        return Object.values(dic);
        // return IterableIterator<Record<string, any>>
    }
    function entries() {
        if (entriesInCustomOrder)
            return entriesInCustomOrder;
        return Object.entries(dic);
        // return IterableIterator<[string, Record<string, any>]>
    }
    function clear() {
        if (!originalObjectToClear)
            return;
        if (entriesInCustomOrder) {
            entriesInCustomOrder.forEach(([key]) => {
                delete originalObjectToClear[key];
            });
        }
        else {
            Object.keys(originalObjectToClear).forEach((key) => {
                delete originalObjectToClear[key];
            });
        }
    }
    function _delete(id) {
        const existed = id in dic;
        if (existed)
            delete dic[id];
        return existed;
    }
    function forEach(callbackfn) {
        const _entries = entriesInCustomOrder || Object.entries(dic);
        _entries.forEach(([k, v]) => {
            callbackfn(v, k, new Map());
        });
    }
    const raw = dic;
    const customMap = {
        size: Object.keys(dic).length,
        get,
        has,
        keys,
        values,
        entries,
        forEach,
        clear,
        delete: _delete,
        raw,
    };
    return customMap;
}

/**
 * a Magnetar plugin is a single function that returns a `PluginInstance`
 * the plugin implements the logic for all actions that a can be called from a Magnetar module instance
 * each action must have the proper for both collection and doc type modules
 */
const CreatePlugin = (vue2StoreOptions) => {
    const { vueInstance: vue } = vue2StoreOptions;
    // this is the local state of the plugin, each plugin that acts as a "local Store Plugin" should have something similar
    // do not define the store plugin data on the top level! Be sure to define it inside the scope of the plugin function!!
    const data = vue.observable({});
    const dataBackups = {};
    const makeBackup = (collectionPath, docId) => {
        // set the backup map for the collection
        if (!(collectionPath in dataBackups))
            dataBackups[collectionPath] = new Map();
        const backupCollectionMap = dataBackups[collectionPath];
        // set the backup array for the doc
        if (!backupCollectionMap.has(docId))
            backupCollectionMap.set(docId, []);
        // make a backup of whatever is found in the data
        const docBackup = copy(data[collectionPath][docId]);
        const arr = backupCollectionMap.get(docId);
        if (docBackup && arr)
            arr.push(docBackup);
    };
    const restoreBackup = (collectionPath, docId) => {
        // set the backup map for the collection
        if (!(collectionPath in dataBackups))
            return;
        const backupCollectionMap = dataBackups[collectionPath];
        // set the backup array for the doc
        if (!backupCollectionMap.has(docId))
            return;
        const docBackupArray = backupCollectionMap.get(docId);
        if (!docBackupArray || !docBackupArray.length) {
            // the backup was "undefined", so we need to delete it
            vue.delete(data[collectionPath], docId);
            return;
        }
        // restore the backup of whatever is found and replace with the data
        const docBackup = docBackupArray.pop();
        if (docBackup)
            vue.set(data[collectionPath], docId, docBackup);
        // the backup was "undefined", so we need to delete it
        if (docBackup === undefined)
            vue.delete(data[collectionPath], docId);
    };
    /**
     * This must be provided by Store Plugins that have "local" data. It is triggered ONCE when the module (doc or collection) is instantiated. In any case, an empty Map for the collectionPath (to be derived from the modulePath) must be set up.
     */
    const modulesAlreadySetup = new Set();
    const setupModule = ({ collectionPath, docId, pluginModuleConfig = {}, }) => {
        const modulePath = [collectionPath, docId].filter(Boolean).join('/');
        if (modulesAlreadySetup.has(modulePath))
            return;
        // always set up a new Map for the **collection**, but only when it is still undefined!
        // the reason for this is that the module can be instantiated for multiple documents in the same collection
        if (!(collectionPath in data))
            vue.set(data, collectionPath, {});
        const dataCollectionDic = data[collectionPath];
        modulesAlreadySetup.add(modulePath);
        // then do anything specific for your plugin, like setting initial data
        const { initialData } = pluginModuleConfig;
        if (!initialData)
            return;
        if (!docId && isArray(initialData)) {
            if (Object.keys(dataCollectionDic).length > 0)
                return;
            for (const [_docId, _docData] of initialData) {
                vue.set(dataCollectionDic, _docId, _docData);
            }
        }
        else if (docId && isPlainObject(initialData)) {
            if (docId in dataCollectionDic)
                return;
            vue.set(dataCollectionDic, docId, initialData);
        }
    };
    /**
     * This must be provided by Store Plugins that have "local" data. It is triggered EVERY TIME the module's data is accessed. The `modulePath` will be either that of a "collection" or a "doc". When it's a collection, it must return a Map with the ID as key and the doc data as value `Map<string, DocDataType>`. When it's a "doc" it must return the doc data directly `DocDataType`.
     */
    const getModuleData = ({ collectionPath, docId, pluginModuleConfig = {}, }) => {
        const dataCollectionDic = data[collectionPath];
        // if it's a doc, return the specific doc
        if (docId)
            return dataCollectionDic[docId];
        // if it's a collection, we must return the dataCollectionDic but with applied query clauses
        // but remember, the return type MUST be a map with id as keys and the docs as value
        const clauses = pick(pluginModuleConfig, ['where', 'orderBy', 'limit']);
        const result = filterDataPerClauses(dataCollectionDic, clauses);
        if (result === 'no-filter') {
            return objectToMap(dataCollectionDic, dataCollectionDic);
        }
        const resultAsDic = Object.fromEntries(result);
        return objectToMap(resultAsDic, dataCollectionDic, result);
    };
    // the plugin must try to implement logic for every `ActionName`
    const fetch = fetchActionFactory(data, vue2StoreOptions);
    const stream = streamActionFactory(data, vue2StoreOptions);
    const insert = insertActionFactory(data, vue2StoreOptions, makeBackup);
    const _merge = writeActionFactory(data, vue2StoreOptions, 'merge', makeBackup);
    const assign = writeActionFactory(data, vue2StoreOptions, 'assign', makeBackup);
    const replace = writeActionFactory(data, vue2StoreOptions, 'replace', makeBackup);
    const deleteProp = deletePropActionFactory(data, vue2StoreOptions, makeBackup);
    const _delete = deleteActionFactory(data, vue2StoreOptions, makeBackup);
    const revert = revertActionFactory(data, vue2StoreOptions, restoreBackup);
    // the plugin function must return a `PluginInstance`
    const instance = {
        revert,
        actions: {
            fetch,
            stream,
            insert,
            merge: _merge,
            assign,
            replace,
            deleteProp,
            delete: _delete,
        },
        setupModule,
        getModuleData,
    };
    return instance;
};

export { CreatePlugin };
